<template>
  <template v-if="showReviewlet">
    <div
      v-if="!interactive && showWriteAReview"
      class="rating"
    >
      <fieldset role="radiogroup">
        <Icon size="s" :icon="csgReviewStarOutlined" />
      </fieldset>
      <span>
        Be the first to
        <span>
          <a
            href="#"
            class="link-body"
            @click="writeAReview"
          >Write A Review</a>
        </span>
      </span>
    </div>
    <div
      v-else
      :class="['rating', { 'rating-small': showReviewCountOnly }]"
    >
      <div v-if="interactive">
        <fieldset
          ref="ratingFieldset"
          aria-label="Star Rating"
          @mouseleave="hoverLeave()"
          @keydown="handleArrowKeys"
        >
          <ul
            role="radiogroup"
            class="rating-list"
          >
            <template
              v-for="index in 5"
              :key="'interactive' + index"
            >
              <li
                :aria-checked="ratingInput >= index"
                role="radio"
              >
                <input
                  :id="'rating' + componentId + index"
                  ref="`rating${componentId}${index}`"
                  v-model="ratingInput"
                  type="radio"
                  tabindex="-1"
                  :aria-label="index + ' Stars'"
                  :name="'rating' + generateRandomId()"
                  :value="index"
                  @change="changeRating(index)"
                >
                <label
                  :tabindex=" index === 1 ? '0':'-1'"
                  :for="'rating' + componentId + index"
                  :class="getStarIconClassName(index)"
                  @mouseover="hover(index)"
                >
                  <Icon
                    :icon="getStarIcon(index)"
                    :size="iconSizeValue"
                  />
                </label>
              </li>
            </template>
          </ul>
        </fieldset>
      </div>
      <template v-else>
        <span
          class="review-star-wrapper"
          role="img"
          :aria-label="`Rated ${rating} stars`"
          tabindex="0"
        >
          <template
            v-for="index in 5"
            :key="'static' + index"
          >
            <Icon
              v-if="partialStarIndex === index"
              :class="[`review-star review-star-${size}`]"
              :icon="partialStarValue > 0 ? csgReviewStarHalfFilled : csgReviewStarOutlined"
              :size="iconSizeValue"
            />

            <Icon
              v-else
              :class="getStarIconClassName(index)"
              :icon="getStarIcon(index)"
              :size="iconSizeValue"
            />
          </template>
        </span>
      </template>
      <div :class="[`rating-block rating-block-${size}`, { 'rating-block-mini': showReviewCountOnly }]">
        <span
          v-if="showTotalRating"
          class="rating-average"
        >
          {{ ratingInput }}
        </span>
        <span
          v-if="showTotalReviews"
          class="rating-description"
        >
          (<component
            :is="reviewLink ? 'a':'span'"
            tabindex="0"
            :aria-labelledby="`${reviewCount > 0 ? ` with ${reviewCount} reviews` : ''}`"
            :class="[{ 'link-body': reviewLink }]"
            :href="reviewLink || null"
          > {{ reviewCount.toLocaleString()
          }}<span v-if="showReviewText">{{ reviewCount > 1 ? ' reviews' : ' review' }}</span></component>)
        </span>
      </div>
    </div>
  </template>
</template>

<script>
import { defineComponent } from 'vue';
import {
  csgReviewStarFilled,
  csgReviewStarHalfFilled,
  csgReviewStarOutlined,
} from '@csgSvgIcons/icons';
import Icon from '../icon';
import generateRandomId from '../../utils/randomNumber';
/**
 * Reviewlet (Star Rating Component)
 * @displayName Reviewlet
 */
export default defineComponent({
  name: 'OUIReviewlet',
  components: {
    Icon,
  },
  props: {
    rating: {
      required: false,
      type: Number,
      default: 0,
    },
    reviewCount: {
      required: false,
      type: Number,
      default: 0,
    },
    interactive: {
      required: false,
      type: Boolean,
      default: false,
    },
    showRating: {
      required: false,
      type: Boolean,
      default: true,
    },
    showReviewsCount: {
      required: false,
      type: Boolean,
      default: true,
    },
    showReviewText: {
      required: false,
      type: Boolean,
      default: true,
    },
    reviewLink: {
      required: false,
      type: String,
      default: null,
    },
    size: {
      required: false,
      type: String,
      default: 'small',
      validator: (type) => type && ['small', 'medium', 'large', 'extra-large'].includes(type), // extra-large bcom only
    },
    handleNoRatings: {
      type: String,
      required: false,
      default: 'hide',
      validator: (type) => type && ['hide', 'writeReviewText'].includes(type),
    },
  },
  emits: ['onchange', 'writeAReview'],
  data() {
    return {
      ratingInput: 0,
      hoverIndex: 0,
      componentId: Math.floor(Math.random() * 1000) + 1,
      csgReviewStarFilled,
      csgReviewStarHalfFilled,
      csgReviewStarOutlined,
    };
  },
  computed: {
    showWriteAReview() {
      return this.handleNoRatings === 'writeReviewText';
    },
    showReviewlet() {
      return this.showWriteAReview
        || this.reviewCount !== 0
        || this.rating !== 0
        || this.interactive;
    },
    showTotalRating() {
      return this.showRating && this.rating !== 0;
    },
    showTotalReviews() {
      return this.showReviewsCount && this.reviewCount !== 0;
    },
    partialStarValue() {
      const fraction = this.rating - parseInt(this.$props.rating, 10);
      return parseInt(fraction * 100, 10);
    },
    partialStarIndex() {
      return parseInt(this.rating, 10) + 1;
    },
    showReviewCountOnly() {
      return !this.showReviewText && !this.showRating;
    },
    iconSizeValue() {
      switch (this.size) {
      case 'medium':
        return 'm';
      case 'large':
        return 'l';
      case 'extra-large':
        return 'xl';
      case 'small':
      default:
        return 's';
      }
    },
  },
  created() {
    this.ratingInput = this.$props.rating;
  },
  methods: {
    generateRandomId,
    hover(index) {
      this.hoverIndex = index;
      return true;
    },
    hoverLeave() {
      this.hoverIndex = 0;
    },
    getStarIcon(index) {
      const rating = this.hoverIndex || this.ratingInput;
      return rating >= index ? csgReviewStarFilled : csgReviewStarOutlined;
    },
    getStarIconClassName(index) {
      const rating = this.hoverIndex || this.ratingInput;
      return rating >= index ? `review-star-filled-${this.size}` : `review-star-${this.size}`;
    },
    changeRating(index) {
      this.ratingInput = index;
      this.$emit('onchange', index);
      return true;
    },
    writeAReview(event) {
      this.$emit('writeAReview');
      event.preventDefault();
      return false;
    },
    handleArrowKeys(event) {
      const { key } = event;
      const radios = this.$refs.ratingFieldset.querySelectorAll('input[type="radio"]');
      const labels = this.$refs.ratingFieldset.querySelectorAll('label');
      const checkedIndex = [...radios].findIndex((radio) => radio.checked);

      if (key === 'ArrowRight' && checkedIndex < radios.length - 1) {
        radios[checkedIndex + 1].checked = true;
        labels[checkedIndex + 1].focus();
        this.changeRating(checkedIndex + 2);
      } else if (key === 'ArrowLeft' && checkedIndex > 0) {
        event.preventDefault();
        radios[checkedIndex - 1].checked = true;
        labels[checkedIndex - 1].focus();
        this.changeRating(checkedIndex);
      } else if (key === 'ArrowLeft' && checkedIndex === 0) {
        event.preventDefault();
        this.changeRating(0);
      }
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@core/csg/src/scss/typography/typography-mixins.scss";

.rating {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  column-gap: rem-calc(8);

  &-small {
    column-gap: rem-calc(4);
  }

  fieldset {
    border: none;
    outline: none;
    padding: 0;
    position: relative;
    margin-inline-start: rem-calc(0);
    margin-inline-end: rem-calc(0);
    line-height: rem-calc(9.6);
    ul {
      list-style-type: none;

      li .review-star-margin-small[for="#rating5"] {
        margin-right: rem-calc(4);
      }
    }
    ul.rating-list li {
      display:inline;
    }
  }

  input[type="radio"] {
    opacity: 0;
    position: fixed;
    width: 0;
  }
}

.rating-block {
  @include body(regular);

  &-mini {
    @include body(small);
  }
}

.review-star-wrapper {
  align-items: center;
  display: flex;
}
</style>
